import {
  GATEWAY_SERVED_BY,
  GRPC_PASSTHROUGH_GATEWAY_SERVED_BY,
} from '@web-platform/shared-universal-constants/src/sonarHeaders';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type {
  InitReq,
  Middleware,
  RequestHandlerFunction,
  RequestMetadata,
} from '@skyscanner-internal/web-http-client-fetch/fetch.pb';

export class CreateSessionAffinityMiddleware implements Middleware {
  private region: string | undefined = undefined;

  handle(
    req: InitReq,
    requestMetadata: RequestMetadata,
    next: RequestHandlerFunction,
  ): Promise<Response> {
    const response = next(req, requestMetadata);
    return response.then((r) => {
      if (!r.ok) {
        return r;
      }

      return r.json().then((body) => {
        // store the region
        const servedBy = r.headers?.get(GATEWAY_SERVED_BY);
        if (servedBy) {
          this.region = servedBy;
        } else {
          logWarn({
            component: this.getName(),
            message: 'No region found in response headers',
          });
        }
        return new Response(JSON.stringify(body));
      });
    });
  }

  getRegion(): string | undefined {
    return this.region;
  }

  // eslint-disable-next-line class-methods-use-this
  getName(): string {
    return 'createSessionAffinityMiddleware';
  }
}

export class PollSessionAffinityMiddleware implements Middleware {
  private readonly getRegion: () => string | undefined;

  constructor(getRegion: () => string | undefined) {
    this.getRegion = getRegion;
  }

  handle(
    req: InitReq,
    requestMetadata: RequestMetadata,
    next: RequestHandlerFunction,
  ): Promise<Response> {
    const region = this.getRegion();

    if (region) {
      const headers = new Headers(req.headers);
      // this is essential for CDN routing to poll session in correct AWS region
      headers.set(GATEWAY_SERVED_BY, region);
      // this is intended to pass information to relevant downstream services (isn't used for session affinity by CDN)
      headers.set(GRPC_PASSTHROUGH_GATEWAY_SERVED_BY, region);
      req.headers = headers;
    } else {
      logWarn({
        component: this.getName(),
        message: 'No region available to set in request headers',
      });
    }
    return next(req, requestMetadata);
  }

  // eslint-disable-next-line class-methods-use-this
  getName(): string {
    return 'pollSessionAffinityMiddleware';
  }
}
